import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { Table, Tag, Typography, Card, Spin, Empty, Input, Space, Button, Select, Tooltip } from 'antd';
import { SwapOutlined, SearchOutlined, ReloadOutlined, ClockCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import Layout from '../../components/AdminLayout';
import { auth } from '@festpix/services';
import { getTransactions, TransactionData } from '../../services/adminWalletService';
import { colors } from '@festpix/ui';

const { Title, Text } = Typography;

export default function TransactionsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [transactions, setTransactions] = useState<TransactionData[]>([]);
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const loadTransactions = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    }
    try {
      const result = await getTransactions();
      setTransactions(result.transactions);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/admin/login');
      } else {
        loadTransactions();
      }
    });
    return unsubscribe;
  }, [navigate, loadTransactions]);

  const handleRefresh = () => {
    loadTransactions(true);
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'purchase':
        return colors.error;
      case 'recharge':
        return colors.success;
      case 'manual_recharge':
        return colors.warning;
      case 'transfer':
        return colors.info;
      case 'refund':
        return colors.warning;
      default:
        return colors.textDescription;
    }
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      purchase: 'Compra',
      recharge: 'Recarga',
      manual_recharge: 'Recarga Manual',
      transfer: 'Transferência',
      refund: 'Estorno',
    };
    return labels[type] || type;
  };

  // Filter transactions based on search and type filter
  const filteredTransactions = useMemo(() => {
    let filtered = transactions;

    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(t => t.type === typeFilter);
    }

    // Apply search filter
    if (searchText.trim()) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.fromWalletId?.toLowerCase().includes(search) ||
          t.toWalletId?.toLowerCase().includes(search) ||
          t.boothName?.toLowerCase().includes(search) ||
          t.performedBy?.email?.toLowerCase().includes(search) ||
          t.performedBy?.name?.toLowerCase().includes(search) ||
          t.id.toLowerCase().includes(search)
      );
    }

    return filtered;
  }, [transactions, searchText, typeFilter]);

  const handleSearch = () => {
    // Search is already reactive via useMemo, this is just for UX feedback
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const columns: ColumnsType<TransactionData> = [
    {
      title: 'Data',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (timestamp) => {
        const date = timestamp?.toDate?.() || new Date();
        return <Text>{date.toLocaleString('pt-BR')}</Text>;
      },
      sorter: (a, b) => {
        const dateA = a.timestamp?.toDate?.() || new Date();
        const dateB = b.timestamp?.toDate?.() || new Date();
        return dateB.getTime() - dateA.getTime();
      },
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      width: 140,
      render: (type) => (
        <Tag color={getTypeColor(type)}>
          {getTypeLabel(type)}
        </Tag>
      ),
    },
    {
      title: 'Valor',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount) => (
        <Text strong style={{ color: colors.success }}>
          R$ {amount.toFixed(2)}
        </Text>
      ),
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: 'De',
      dataIndex: 'fromWalletId',
      key: 'fromWalletId',
      width: 150,
      render: (id) => (
        id ? (
          <Text code style={{ fontSize: 12 }}>
            {id?.substring(0, 8)}...
          </Text>
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
    },
    {
      title: 'Para',
      key: 'toWalletId',
      width: 180,
      render: (_, record) => (
        record.toWalletId ? (
          record.boothName ? (
            <Text strong style={{ color: colors.info }}>
              {record.boothName}
            </Text>
          ) : (
            <Text code style={{ fontSize: 12 }}>
              {record.toWalletId?.substring(0, 8)}...
            </Text>
          )
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
    },
    {
      title: 'Realizado Por',
      dataIndex: 'performedBy',
      key: 'performedBy',
      width: 200,
      render: (performedBy) => (
        performedBy ? (
          <div>
            {performedBy.name && (
              <Text strong style={{ display: 'block', fontSize: 12 }}>
                {performedBy.name}
              </Text>
            )}
            <Text type="secondary" style={{ fontSize: 11 }}>
              {performedBy.email}
            </Text>
          </div>
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
    },
  ];

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          <SwapOutlined style={{ marginRight: 12 }} />
          Transações
        </Title>
        <Space>
          {lastUpdated && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              Atualizado às {formatTime(lastUpdated)}
            </Text>
          )}
          <Tooltip title="Atualizar lista de transações">
            <Button
              icon={<ReloadOutlined spin={refreshing} />}
              onClick={handleRefresh}
              loading={refreshing}
            >
              Atualizar
            </Button>
          </Tooltip>
        </Space>
      </div>

      <Card style={{ background: colors.cardBackground }}>
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="Buscar por carteira, estande ou e-mail"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            style={{ width: 300 }}
            allowClear
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
          >
            Buscar
          </Button>
          <Select
            value={typeFilter}
            onChange={setTypeFilter}
            style={{ width: 180 }}
            options={[
              { value: 'all', label: 'Todos os Tipos' },
              { value: 'purchase', label: 'Compra' },
              { value: 'recharge', label: 'Recarga' },
              { value: 'manual_recharge', label: 'Recarga Manual' },
              { value: 'transfer', label: 'Transferência' },
              { value: 'refund', label: 'Estorno' },
            ]}
          />
        </Space>
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">
            Mostrando {filteredTransactions.length} de {transactions.length} transações
          </Text>
        </div>
        <Table
          columns={columns}
          dataSource={filteredTransactions}
          rowKey="id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} transações`,
          }}
          locale={{
            emptyText: <Empty description="Nenhuma transação encontrada" />,
          }}
        />
      </Card>
    </Layout>
  );
}
